import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { categoriesAPI, articlesAPI } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowLeft, FolderOpen, FileText, Shield, X, Check, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';

const generateSlug = (name) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const AdminCategories = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [articleCounts, setArticleCounts] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', description: '' });

  const fetchCategories = useCallback(async () => {
    try {
      const res = await categoriesAPI.getAll(true);
      setCategories(res.data);
      // Fetch article counts
      const counts = {};
      for (const cat of res.data) {
        try {
          const countRes = await categoriesAPI.getArticleCount(cat.id);
          counts[cat.id] = countRes.data.article_count;
        } catch {
          counts[cat.id] = 0;
        }
      }
      setArticleCounts(counts);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const resetForm = () => {
    setFormData({ name: '', slug: '', description: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: editingId ? prev.slug : generateSlug(name),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.slug.trim()) {
      toast.error('Name and slug are required');
      return;
    }
    // Check for duplicates
    const duplicate = categories.find(
      c => c.slug === formData.slug && c.id !== editingId
    );
    if (duplicate) {
      toast.error('A category with this slug already exists');
      return;
    }
    try {
      if (editingId) {
        await categoriesAPI.update(editingId, formData);
        toast.success('Category updated');
      } else {
        await categoriesAPI.create(formData);
        toast.success('Category created');
      }
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const startEdit = (cat) => {
    setFormData({ name: cat.name, slug: cat.slug, description: cat.description || '' });
    setEditingId(cat.id);
    setShowForm(true);
  };

  const handleDeactivate = async (cat) => {
    const count = articleCounts[cat.id] || 0;
    if (count > 0) {
      toast.error(`Cannot deactivate: ${count} article(s) use this category. Reassign them first.`);
      setDeleteConfirm(null);
      return;
    }
    try {
      await categoriesAPI.deactivate(cat.id, null);
      toast.success('Category deactivated');
      setDeleteConfirm(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const handleActivate = async (catId) => {
    try {
      await categoriesAPI.activate(catId);
      toast.success('Category activated');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to activate');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const activeCategories = categories.filter(c => c.is_active !== false);
  const inactiveCategories = categories.filter(c => c.is_active === false);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-categories-page">
      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-gray-200 rounded-lg transition-colors" data-testid="back-to-admin">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-playfair font-bold text-gray-900" data-testid="page-title">Categories</h1>
              <p className="text-sm text-gray-500 mt-1">{activeCategories.length} active categories</p>
            </div>
          </div>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-brand-red text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-brand-red/90 transition-colors"
            data-testid="add-category-button"
          >
            <Plus className="w-4 h-4" />
            Add Category
          </button>
        </div>

        {/* Create / Edit Form */}
        {showForm && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8 shadow-sm" data-testid="category-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">
                {editingId ? 'Edit Category' : 'New Category'}
              </h2>
              <button onClick={resetForm} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent"
                    placeholder="e.g. Deals & Offers"
                    required
                    data-testid="category-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent font-mono text-sm"
                    placeholder="deals-offers"
                    required
                    data-testid="category-slug-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent"
                  placeholder="Brief description of this category"
                  rows={2}
                  data-testid="category-description-input"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-brand-red/90 transition-colors"
                  data-testid="save-category-button"
                >
                  <Check className="w-4 h-4" />
                  {editingId ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2.5 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Categories Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="categories-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Slug</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Articles</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {activeCategories.length === 0 && inactiveCategories.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                    <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p className="text-lg font-medium">No categories yet</p>
                    <p className="text-sm">Create your first category to get started.</p>
                  </td>
                </tr>
              ) : (
                <>
                  {activeCategories.map((cat) => (
                    <tr key={cat.id} className="hover:bg-gray-50 transition-colors" data-testid={`category-row-${cat.slug}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-brand-red/10 flex items-center justify-center">
                            <Tag className="w-4 h-4 text-brand-red" />
                          </div>
                          <span className="font-semibold text-gray-900">{cat.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <code className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">{cat.slug}</code>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                          <FileText className="w-3.5 h-3.5" />
                          {articleCounts[cat.id] ?? '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">Active</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => startEdit(cat)}
                            className="p-2 text-gray-500 hover:text-brand-red hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit"
                            data-testid={`edit-category-${cat.slug}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {deleteConfirm === cat.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDeactivate(cat)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg text-xs font-semibold"
                                data-testid={`confirm-delete-${cat.slug}`}
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg text-xs"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(cat.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Deactivate"
                              data-testid={`delete-category-${cat.slug}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {inactiveCategories.map((cat) => (
                    <tr key={cat.id} className="bg-gray-50/50 opacity-60" data-testid={`category-row-${cat.slug}-inactive`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center">
                            <Tag className="w-4 h-4 text-gray-400" />
                          </div>
                          <span className="font-semibold text-gray-500">{cat.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <code className="text-sm text-gray-400 bg-gray-100 px-2 py-1 rounded">{cat.slug}</code>
                      </td>
                      <td className="px-6 py-4 text-center text-sm text-gray-400">{articleCounts[cat.id] ?? '—'}</td>
                      <td className="px-6 py-4 text-center">
                        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-200 text-gray-500">Inactive</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleActivate(cat.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                          data-testid={`activate-category-${cat.slug}`}
                        >
                          <Shield className="w-3 h-3" />
                          Activate
                        </button>
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminCategories;
